import {
  Component,
  forwardRef,
  Input,
  Renderer,
  ElementRef,
  OnChanges,
  DoCheck,
  ViewChild,
  NgModule,
  OpaqueToken,
  Optional,
  Inject,
  EventEmitter,
  Output,
  ViewEncapsulation,
  ModuleWithProviders,
  SimpleChanges
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor
} from '@angular/forms';

import { toBoolean } from '../common/boolean-property';
import { toNumber } from '../common/number.property';
import { MdlButtonModule } from '../button/mdl-button.component';
import { MdlIconModule } from '../icon/mdl-icon.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

export const DISABLE_NATIVE_VALIDITY_CHECKING = new OpaqueToken('disableNativeValidityChecking');


const noop = (_?: any) => {};
let nextId = 0;

const IS_FOCUSED = 'is-focused';
const IS_DISABLED = 'is-disabled';
const IS_INVALID = 'is-invalid';
const IS_DIRTY = 'is-dirty';


export class MdlTextFieldComponent implements ControlValueAccessor, OnChanges, DoCheck {
  private value_: any;
  private el: HTMLElement;

  
  public blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  
  public focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  
  public keyupEmitter: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

   public inputEl: ElementRef;

  get value(): any { return this.value_; };
   set value(v: any) {
    this.value_ = this.type === 'number' ? ( v === '' ? null : parseFloat(v)) : v;
    this.onChangeCallback(this.value);
  }

   public type = 'text';
   public label;
   public pattern;
   public min;
   public max;
   public step;
   public name;
   public id = `mdl-textfield-${nextId++}`;
   public errorMessage;

  private _disabled: boolean = false;
  
  get disabled(): boolean { return this._disabled; }
  set disabled(value) { this._disabled = toBoolean(value); }

  private _readonly: boolean = false;
  
  get readonly() { return this._readonly; }
  set readonly(value) { this._readonly = toBoolean(value); }

  private _required: boolean = false;
  
  get required() { return this._required; }
  set required(value) { this._required = toBoolean(value); }

  private _autofocus: boolean = false;
  
  get autofocus() { return this._autofocus; }
  set autofocus(value) { this._autofocus = toBoolean(value); }

  private _isFloatingLabel: boolean = false;
  
  get isFloatingLabel() { return this._isFloatingLabel; }
  set isFloatingLabel(value) { this._isFloatingLabel = toBoolean(value); }

   public placeholder: string;
   public autocomplete: string;

  private _rows: number = null;
  
  get rows() { return this._rows; }
  set rows(value) { this._rows = toNumber(value); }

  private _maxrows: number = -1;
  
  get maxrows() { return this._maxrows; }
  set maxrows(value) { this._maxrows = toNumber(value); }
   public icon: string;

   public tabindex: number = null;

   public maxlength: number = null;


  // @experimental
  private _disableNativeValidityChecking: boolean = false;
  
  get disableNativeValidityChecking() { return this._disableNativeValidityChecking; }
  set disableNativeValidityChecking(value) { this._disableNativeValidityChecking = toBoolean(value);}

  constructor(
    private renderer: Renderer,
    private elmRef: ElementRef,
      private nativeCheckGlobalDisabled: Boolean) {
    this.el = elmRef.nativeElement;
  }

  public writeValue(value: any): void {
    this.value_ = value;
    this.checkDirty();
  }

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  public registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.checkDisabled();
  }

  public ngDoCheck() {
    this.checkValidity();
    this.checkDirty();
  }

  public setFocus() {
    if ( !this.inputEl ) {
      return;
    }
    this.renderer.invokeElementMethod(this.inputEl.nativeElement, 'focus', []);
  }

  protected onFocus(event: FocusEvent) {
    this.renderer.setElementClass(this.el, IS_FOCUSED, true);
    this.focusEmitter.emit(event);
  }

  protected onBlur(event: FocusEvent) {
    this.renderer.setElementClass(this.el, IS_FOCUSED, false);
    this.onTouchedCallback();
    this.blurEmitter.emit(event);
  }

  protected onKeyup(event: KeyboardEvent) {
    this.keyupEmitter.emit(event);
  }

  private checkDisabled() {
    this.renderer.setElementClass(this.el, IS_DISABLED, this.disabled);
  }

  private checkValidity() {
    // check the global setting - if globally disabled do no check
    if ( this.nativeCheckGlobalDisabled === true ) {
      return;
    }
    // check local setting - if locally disabled do no check
    if ( this.disableNativeValidityChecking ) {
      return;
    }
    if (this.inputEl && this.inputEl.nativeElement.validity) {
      this.renderer.setElementClass(this.el, IS_INVALID, !this.inputEl.nativeElement.validity.valid);
    }
  }

  private checkDirty() {
    let dirty = this.inputEl && this.inputEl.nativeElement.value && this.inputEl.nativeElement.value.length > 0;
    this.renderer.setElementClass(this.el, IS_DIRTY, dirty);
  }

  public keydownTextarea($event: KeyboardEvent) {
    var currentRowCount = this.inputEl.nativeElement.value.split('\n').length;
    if ($event.keyCode === 13) {
      if (currentRowCount >= this.maxrows && this.maxrows !== -1) {
        $event.preventDefault();
      }
    }
  }

  // hm only for test purposes to simulate a change to the input field that will change the
  // model value.
  triggerChange(event: Event) {
    this.value = (event.target as HTMLInputElement).value;
    this.onTouchedCallback();
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'mdl-textfield',
  host: {
    '[class.mdl-textfield]': 'true',
    '[class.is-upgraded]': 'true',
    '[class.mdl-textfield--expandable]': 'icon',
    '[class.mdl-textfield--floating-label]': 'isFloatingLabel',
    '[class.has-placeholder]': 'placeholder'
  },
  template: `
   <div *ngIf="!icon">
     <textarea
        *ngIf="rows"
        #input
        [rows]="rows"
        class="mdl-textfield__input"
        type="text"
        [attr.name]="name"
        [id]="id"
        [placeholder]="placeholder ? placeholder : ''"
        (focus)="onFocus($event)"
        (blur)="onBlur($event)"
        (keydown)="keydownTextarea($event)"
        (keyup)="onKeyup($event)"
        [(ngModel)]="value"
        [disabled]="disabled"
        [required]="required"
        [autofocus]="autofocus"
        [readonly]="readonly"
        ></textarea>
     <input
        *ngIf="!rows"
        #input
        class="mdl-textfield__input"
        [type]="type"
        [attr.name]="name"
        [id]="id"
        [pattern]="pattern ? pattern : '.*'"
        [attr.min]="min"
        [attr.max]="max"
        [attr.step]="step"
        [placeholder]="placeholder ? placeholder : ''"
        [autocomplete]="autocomplete ? autocomplete : ''"
        (focus)="onFocus($event)"
        (blur)="onBlur($event)"
        (keyup)="onKeyup($event)"
        [(ngModel)]="value"
        [disabled]="disabled"
        [required]="required"
        [autofocus]="autofocus"
        [readonly]="readonly"
        [attr.tabindex]="tabindex"
        [maxlength]="maxlength"
        >
     <label class="mdl-textfield__label" [attr.for]="id">{{label}}</label>
     <span class="mdl-textfield__error">{{errorMessage}}</span>
   </div>
   <div *ngIf="icon">
      <button mdl-button mdl-button-type="icon" (click)="setFocus()">
         <mdl-icon>{{icon}}</mdl-icon>
      </button>
      <div class="mdl-textfield__expandable-holder">
       <input
          #input
          class="mdl-textfield__input"
          [type]="type"
          [attr.name]="name"
          [id]="id"
          [pattern]="pattern ? pattern : '.*'"
          [attr.min]="min"
          [attr.max]="max"
          [attr.step]="step"
          [placeholder]="placeholder ? placeholder : ''"
          [autocomplete]="autocomplete ? autocomplete : ''"
          (focus)="onFocus($event)"
          (blur)="onBlur($event)"
          (keyup)="onKeyup($event)"
          [(ngModel)]="value"
          [disabled]="disabled"
          [required]="required"
          [autofocus]="autofocus"
          [readonly]="readonly"
          [attr.tabindex]="tabindex"
          [maxlength]="maxlength"
         >
     <label class="mdl-textfield__label" [attr.for]="id">{{label}}</label>
     <span class="mdl-textfield__error">{{errorMessage}}</span>
      </div>
   </div>
   `,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MdlTextFieldComponent),
    multi: true
  }],
  encapsulation: ViewEncapsulation.None
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: Boolean, decorators: [{ type: Optional }, { type: Inject, args: [DISABLE_NATIVE_VALIDITY_CHECKING, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'blurEmitter': [{ type: Output, args: ['blur', ] },],
'focusEmitter': [{ type: Output, args: ['focus', ] },],
'keyupEmitter': [{ type: Output, args: ['keyup', ] },],
'inputEl': [{ type: ViewChild, args: ['input', ] },],
'value': [{ type: Input },],
'type': [{ type: Input },],
'label': [{ type: Input },],
'pattern': [{ type: Input },],
'min': [{ type: Input },],
'max': [{ type: Input },],
'step': [{ type: Input },],
'name': [{ type: Input },],
'id': [{ type: Input },],
'errorMessage': [{ type: Input, args: ['error-msg', ] },],
'disabled': [{ type: Input },],
'readonly': [{ type: Input },],
'required': [{ type: Input },],
'autofocus': [{ type: Input },],
'isFloatingLabel': [{ type: Input, args: ['floating-label', ] },],
'placeholder': [{ type: Input },],
'autocomplete': [{ type: Input },],
'rows': [{ type: Input },],
'maxrows': [{ type: Input },],
'icon': [{ type: Input },],
'tabindex': [{ type: Input },],
'maxlength': [{ type: Input },],
'disableNativeValidityChecking': [{ type: Input },],
};
}



export class MdlTextFieldModule {
  public static forRoot(): ModuleWithProviders {
    return {
      ngModule: MdlTextFieldModule,
      providers: []
    };
  }
static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [MdlIconModule, MdlButtonModule, FormsModule, CommonModule],
  exports: [MdlTextFieldComponent],
  declarations: [MdlTextFieldComponent],
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
